/**
 * Monthly Reports RTK Query service.
 *
 * Since schema v3 reports are LIVE: server-side triggers recompute the month
 * doc whenever underlying appointments/transactions change (including
 * retroactive edits to past months).
 *
 * Freshness model:
 *  - getMonthlyReport keeps a Firestore onSnapshot open for the cached month
 *    (onCacheEntryAdded) — the page updates the moment a recompute lands,
 *    with no polling. The server debounces recomputes by ~60s, so
 *    refetch-on-mutation would race the write; the snapshot doesn't.
 *  - getMonthlyReportsList (trend charts) is fetch-based and is
 *    cross-invalidated from financeApi/calendarApi mutations.
 */

import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  getMonthlyReportsList,
  getMonthlyReport,
  getMonthlyReportsListScoped,
  getMonthlyReportScoped,
  triggerMonthlyReportGeneration,
  ensureMonthlyReportGeneration,
  subscribeMonthlyReport,
} from "../../firebase";

// Multi-clinic v2: `clinicWide` (admin / reports.viewClinicWide — pass
// `hasClinicWideScope` from the page) reads the doc directly and keeps the
// live snapshot; `clinicWide: false` goes through the scoped callable (rules
// deny the doc) and is fetch-based (invalidated by the finance/calendar
// cross-tags). Omitted ⇒ true (legacy callers were admins).

export const monthlyReportsApi = createApi({
  reducerPath: "monthlyReportsApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["MonthlyReport"],
  endpoints: (builder) => ({
    getMonthlyReportsList: builder.query({
      async queryFn({ clinicId, limit, clinicWide = true }) {
        try {
          const list = clinicWide
            ? await getMonthlyReportsList(clinicId, limit || 24)
            : await getMonthlyReportsListScoped(clinicId, limit || 24);
          return { data: list };
        } catch (error) {
          console.error("[CK-7903] Error in getMonthlyReportsList queryFn:", {
            errorCode: "CK-7903",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ monthKey }) => ({
                type: "MonthlyReport",
                id: monthKey,
              })),
              { type: "MonthlyReport", id: "LIST" },
            ]
          : [{ type: "MonthlyReport", id: "LIST" }],
    }),

    getMonthlyReport: builder.query({
      async queryFn({ clinicId, monthKey, clinicWide = true }) {
        try {
          const report = clinicWide
            ? await getMonthlyReport(clinicId, monthKey)
            : await getMonthlyReportScoped(clinicId, monthKey);
          return { data: report };
        } catch (error) {
          console.error("[CK-7904] Error in getMonthlyReport queryFn:", {
            errorCode: "CK-7904",
            errorMessage: error.message,
            clinicId,
            monthKey,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      // Live doc: keep an onSnapshot open for as long as this month is cached
      async onCacheEntryAdded(
        { clinicId, monthKey, clinicWide = true },
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
      ) {
        let unsubscribe = null;
        try {
          await cacheDataLoaded;
          // Scoped readers cannot listen to the doc (rules) — fetch-based only.
          if (clinicWide === false) {
            await cacheEntryRemoved;
            return;
          }
          unsubscribe = subscribeMonthlyReport(
            clinicId,
            monthKey,
            (report) => {
              updateCachedData(() => report);
            },
            (error) => {
              console.error("[CK-7915] Monthly report snapshot error:", {
                errorCode: "CK-7915",
                errorMessage: error.message,
                clinicId,
                monthKey,
                timestamp: new Date().toISOString(),
              });
            }
          );
        } catch {
          // cacheDataLoaded rejects if the cache entry is removed before the
          // initial fetch resolves — nothing to subscribe to.
        }
        await cacheEntryRemoved;
        if (unsubscribe) unsubscribe();
      },
      providesTags: (result, error, { monthKey }) => [
        { type: "MonthlyReport", id: monthKey },
      ],
    }),

    // Lazy backfill: create/upgrade a month doc on view (any clinic member)
    ensureMonthlyReport: builder.mutation({
      async queryFn({ clinicId, year, month, forceRecompute }) {
        try {
          const data = await ensureMonthlyReportGeneration({
            clinicId,
            year,
            month,
            forceRecompute,
          });
          return { data };
        } catch (error) {
          console.error("[CK-7916] Error in ensureMonthlyReport mutation:", {
            errorCode: "CK-7916",
            errorMessage: error.message,
            clinicId,
            year,
            month,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { year, month }) => {
        const monthKey = `${year}-${String(month).padStart(2, "0")}`;
        return [
          { type: "MonthlyReport", id: monthKey },
          { type: "MonthlyReport", id: "LIST" },
        ];
      },
    }),

    triggerReportGeneration: builder.mutation({
      async queryFn({ clinicId, year, month, notify }) {
        try {
          const data = await triggerMonthlyReportGeneration({
            clinicId,
            year,
            month,
            notify,
          });
          return { data };
        } catch (error) {
          console.error("[CK-7905] Error in triggerReportGeneration mutation:", {
            errorCode: "CK-7905",
            errorMessage: error.message,
            clinicId,
            year,
            month,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { year, month }) => {
        const monthKey = `${year}-${String(month).padStart(2, "0")}`;
        return [
          { type: "MonthlyReport", id: monthKey },
          { type: "MonthlyReport", id: "LIST" },
        ];
      },
    }),
  }),
});

export const {
  useGetMonthlyReportsListQuery,
  useGetMonthlyReportQuery,
  useEnsureMonthlyReportMutation,
  useTriggerReportGenerationMutation,
} = monthlyReportsApi;
